import React, { useEffect, useState } from "react";
import { X, Users, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { modeColor } from "@/lib/gamemode";

const MODE_LABEL = { hardcore: "HCIM", ultimate: "UIM", ironman: "IM", group: "GIM", hardcore_group: "HC GIM", main: "Main" };

export default function OnlineNow({ count = 0 }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/public/online-list")
      .then(({ data }) => setUsers(data.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [open]);

  return (
    <>
      <button
        data-testid="online-counter"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 osrs-panel-plain shrink-0 whitespace-nowrap px-2.5 py-1 text-xs cursor-pointer hover:brightness-125 transition-[filter]"
        title={`${count} adventurer${count === 1 ? "" : "s"} online — click to see who`}
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ade80] opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e]" />
        </span>
        <span className="osrs-num text-[#7CFC7C]">{count}</span>
        <span className="osrs-shadow text-[#b9a06a] hidden sm:inline">online</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 px-4 py-10 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-5 w-full max-w-sm my-auto" onClick={(e) => e.stopPropagation()} data-testid="online-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="osrs-title text-xl flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4ade80] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#22c55e]" />
                </span>
                Online now <span className="osrs-num text-[#7CFC7C] text-base">({users.length})</span>
              </h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-online"><X size={16} /></button>
            </div>

            {loading && users.length === 0 ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8" data-testid="online-loading">Scanning Gielinor...</p>
            ) : users.length === 0 ? (
              <p className="osrs-shadow text-[#b9a06a] text-center py-8" data-testid="online-empty">No one else around right now.</p>
            ) : (
              <ul className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1" data-testid="online-list">
                {users.map((u, i) => {
                  const inner = (
                    <>
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22c55e] shrink-0 shadow-[0_0_5px_#4ade80]" />
                        <span className="osrs-shadow text-[#E6A519] text-sm truncate">{u.name}</span>
                        {u.is_me && <span className="text-[#00FF00] text-[10px]">(you)</span>}
                      </span>
                      {u.rsn && (
                        <span className="osrs-num text-xs shrink-0 flex items-center gap-1" style={{ color: modeColor(u.gamemode) }}>
                          {u.rsn}<span className="text-[9px] uppercase text-[#8a7a54]">{MODE_LABEL[u.gamemode] || "Main"}</span>
                          <ArrowUpRight size={12} className="text-[#8a7a54]" />
                        </span>
                      )}
                    </>
                  );
                  const base = "flex items-center justify-between gap-2 border px-3 py-2";
                  if (u.rsn) {
                    return (
                      <li key={i}>
                        <a
                          href={`/u/${encodeURIComponent(u.rsn)}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`online-user-${i}`}
                          title={`View ${u.rsn}'s public rank card`}
                          className={`${base} bg-black/30 hover:bg-black/50 hover:brightness-110 transition-[filter,background-color] ${u.is_me ? "border-[#3f6a2a]" : "border-[#0f0d09]"}`}
                        >
                          {inner}
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={i} className={`${base} bg-black/30 ${u.is_me ? "border-[#3f6a2a]" : "border-[#0f0d09]"}`} data-testid={`online-user-${i}`}>
                      {inner}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
